import {
  BEERPONG_ARC_RATIO,
  BEERPONG_BALL_RADIUS,
  BEERPONG_CENTER_Y,
  BEERPONG_CUP_HEIGHT,
  BEERPONG_CUP_RADIUS,
  BEERPONG_GRAVITY,
  BEERPONG_TABLE_LENGTH,
  BEERPONG_TABLE_WIDTH,
  BEERPONG_THROW_X,
  beerPongRack,
  beerPongRange,
  beerPongThrowSpeed,
  normalizeAim,
  quantizeBeerPong,
  type BeerPongBallPoint,
  type BeerPongThrowOutcome,
} from '@awwyeah/shared';

/**
 * The Beer Pong simulator (WS-17f) — the only file in the app that knows how a throw
 * actually flies. It lives in the web app rather than `@awwyeah/shared` for the same reason
 * Pool's does: the server's push notifier and the rules engine work off the recorded outcome
 * snapshot (`packages/shared/src/modules/games/beerpong.ts`), so Node never needs a
 * simulator and this never reaches the server bundle.
 *
 * ## Why this isn't Planck, even though Planck is already vendored
 *
 * Pool reaches for the vendored Planck because a pool shot genuinely is a 2D problem — many
 * bodies, sliding on a plane, hitting each other. A Beer Pong throw is the opposite shape:
 * **one** body, whose entire story is the height axis a 2D engine doesn't have. It flies over
 * the cups, and the only interaction it ever has is a single test on the way down. Putting
 * that in a planar solver would mean faking the third axis by hand anyway and buying a
 * general contact solver to move one circle.
 *
 * So: ~150 lines of explicit 3-DOF integration, fixed timestep, and — the part that matters —
 * arithmetic that is *entirely* `+ - * /` and `Math.sqrt`, every one of them exactly specified
 * by ECMA-262 and therefore bit-identical on V8, JavaScriptCore and SpiderMonkey. Pool has to
 * hedge on `Math.sin`/`Math.cos` (implementation-approximated) when it converts an aim angle
 * into a velocity; we sidestep that entirely by recording the aim as a raw vector and
 * normalising it with `Math.sqrt`. This simulator should be *exactly* reproducible
 * cross-device, not probably-reproducible.
 *
 * We keep the thrower's snapshot as the authority regardless (`playback.ts`'s `replayThrow`),
 * because "should be" is doing real work in that sentence and a family game where two phones
 * show a different number of cups is broken in a way that being right about floats doesn't
 * fix. The divergence warning is how we find out whether the claim holds in the wild.
 *
 * ## Fixed timestep, decoupled render
 *
 * Same split as Pool: `simulateThrow` runs the whole flight to rest up front in fixed
 * `BEERPONG_PHYSICS_DT` steps, recording a position sample every `BEERPONG_FRAME_STRIDE`
 * steps; `playThrow` (playback.ts) maps wall-clock time onto that buffer on
 * `requestAnimationFrame`. No clock, no frame rate and no `devicePixelRatio` ever reaches
 * the integrator, so a backgrounded tab, a 120 Hz iPad and a throttled old phone all produce
 * identical results.
 */

/** 120 Hz — a flight is a few hundred steps, and a rim clip lands on the right side of the
 * cup lip rather than tunnelling past it. */
export const BEERPONG_PHYSICS_DT = 1 / 120;
/** Record a sample every other step → a 60 Hz buffer for playback. */
export const BEERPONG_FRAME_STRIDE = 2;
/** Hard ceiling so a pathological throw can't hang the UI. 8 s of simulated time. */
const MAX_STEPS = Math.round(8 / BEERPONG_PHYSICS_DT);

/** A rim strike keeps a little of its sideways speed, always leaves the lip outward, and
 * pops up — which is what makes a rattle able to find a neighbouring cup. */
const RIM_TANGENT = 0.6;
const RIM_RADIAL = 0.45;
const RIM_MIN_KICK = 0.25;
const RIM_BOUNCE_UP = 0.5;
/** Hitting the outside of a cup below the lip: mostly a stop, slightly a deflection. */
const CUP_WALL_RESTITUTION = 0.45;
/** The table itself — a ping-pong ball on plywood, damped enough that a bounce shot is a
 * real (if optimistic) option rather than a pinball. */
const TABLE_RESTITUTION = 0.5;
const TABLE_FRICTION = 0.72;
/** Below this vertical speed a bounce becomes a roll — exponential bouncing never actually
 * stops, and "nearly settled" is not a reproducible resting place. */
const ROLL_THRESHOLD = 0.4;
const ROLL_FRICTION = 0.985;
/** Below this, the ball is at rest. */
const REST_SPEED = 0.12;

/** One sampled instant of a throw. A single ball, so a frame is a single point. */
export type BeerPongFrame = BeerPongBallPoint;

export interface BeerPongSimulation {
  outcome: BeerPongThrowOutcome;
  /** Position samples at `BEERPONG_PHYSICS_DT * BEERPONG_FRAME_STRIDE` intervals, first =
   * the ball leaving the hand, last = in a cup or at rest. */
  frames: BeerPongFrame[];
  /** How long the throw actually took, in simulated milliseconds. */
  durationMs: number;
}

export interface BeerPongThrowInput {
  /** Aim as a raw vector in the thrower's frame — see the docblock on why not an angle. */
  aimX: number;
  aimY: number;
  power: number;
}

const CAPTURE_RADIUS = BEERPONG_CUP_RADIUS - BEERPONG_BALL_RADIUS;
const RIM_RADIUS = BEERPONG_CUP_RADIUS + BEERPONG_BALL_RADIUS;

/** The cup nearest `point`, if it's within `radius`, searched in ascending cup index so a tie
 * resolves the same way on every device. */
function nearestCup(
  cups: readonly { x: number; y: number }[],
  standing: readonly number[],
  x: number,
  y: number,
  radius: number,
): { cup: number; distance: number } | null {
  let best: { cup: number; distance: number } | null = null;
  for (const cup of standing) {
    const centre = cups[cup];
    if (!centre) continue;
    const dx = x - centre.x;
    const dy = y - centre.y;
    const distanceSq = dx * dx + dy * dy;
    if (distanceSq > radius * radius) continue;
    const distance = Math.sqrt(distanceSq);
    if (!best || distance < best.distance) best = { cup, distance };
  }
  return best;
}

/**
 * Runs one throw from the thrower's hand to a cup or to rest, and returns both the
 * rules-facing outcome snapshot and the sample buffer the animation plays back. `standing` is
 * the **defender's** remaining cups (a cup already down neither catches the ball nor blocks
 * it), in the thrower's own frame — the mirror that makes both ends of the table one code
 * path lives in `@awwyeah/shared`'s `mirrorPoint`.
 *
 * Pure with respect to its inputs: no module state, no clock, no randomness — call it twice
 * with the same arguments and you get the same numbers, which is what
 * `apps/web/test/games/beerpong.determinism.test.ts` pins down.
 */
export function simulateThrow(
  standing: readonly number[],
  shot: BeerPongThrowInput,
): BeerPongSimulation {
  const cups = beerPongRack();
  // Ascending, and a copy: the caller's array order must never reach the physics.
  const remaining = [...standing].sort((a, b) => a - b);

  const unit = normalizeAim(shot.aimX, shot.aimY);
  const speed = beerPongThrowSpeed(beerPongRange(shot.power));
  let px = BEERPONG_THROW_X;
  let py = BEERPONG_CENTER_Y;
  let pz = BEERPONG_BALL_RADIUS;
  let vx = unit.x * speed;
  let vy = unit.y * speed;
  let vz = BEERPONG_ARC_RATIO * speed;
  let rolling = false;

  const rimmed: number[] = [];
  const frames: BeerPongFrame[] = [{ x: px, y: py, z: pz }];
  let made: number | null = null;
  let steps = 0;

  for (; steps < MAX_STEPS; steps += 1) {
    const wasZ = pz;
    const wasX = px;
    const wasY = py;

    if (rolling) {
      vx *= ROLL_FRICTION;
      vy *= ROLL_FRICTION;
    } else {
      // Exact for constant acceleration over the step (`z += v·dt − ½g·dt²`), not plain
      // Euler. Not a micro-optimisation: Euler's per-step error accumulates over a
      // second-long flight into a systematic few-centimetre bias, and the aiming reticle is
      // computed from the *closed form* (`beerPongLanding`). A reticle that is quietly wrong
      // by half a cup in one direction is the kind of unfairness a player can feel and never
      // diagnose. This way the integrator lands exactly where the reticle promised.
      pz +=
        vz * BEERPONG_PHYSICS_DT -
        0.5 * BEERPONG_GRAVITY * BEERPONG_PHYSICS_DT * BEERPONG_PHYSICS_DT;
      vz -= BEERPONG_GRAVITY * BEERPONG_PHYSICS_DT;
    }
    px += vx * BEERPONG_PHYSICS_DT;
    py += vy * BEERPONG_PHYSICS_DT;

    // --- coming down through the lip: the one moment a cup can catch the ball -----------
    if (wasZ > BEERPONG_CUP_HEIGHT && pz <= BEERPONG_CUP_HEIGHT && vz < 0) {
      // Where it crossed, not where the step happened to land — a 120 Hz step is 3cm of
      // travel and the capture window is 4cm wide.
      const t = (wasZ - BEERPONG_CUP_HEIGHT) / (wasZ - pz);
      const crossX = wasX + (px - wasX) * t;
      const crossY = wasY + (py - wasY) * t;

      const hit = nearestCup(cups, remaining, crossX, crossY, RIM_RADIUS);
      if (hit && hit.distance <= CAPTURE_RADIUS) {
        made = hit.cup;
        const centre = cups[hit.cup] as { x: number; y: number };
        // Two extra samples so the animation drops it *into* the cup instead of stopping on
        // the lip — the ball has visibly gone somewhere by the time the frame buffer ends.
        frames.push({ x: crossX, y: crossY, z: BEERPONG_CUP_HEIGHT });
        frames.push({ x: centre.x, y: centre.y, z: BEERPONG_CUP_HEIGHT * 0.45 });
        frames.push({ x: centre.x, y: centre.y, z: BEERPONG_BALL_RADIUS });
        px = centre.x;
        py = centre.y;
        pz = BEERPONG_BALL_RADIUS;
        steps += 1;
        break;
      }
      if (hit && hit.distance > 0) {
        const centre = cups[hit.cup] as { x: number; y: number };
        const nx = (crossX - centre.x) / hit.distance;
        const ny = (crossY - centre.y) / hit.distance;
        const radial = vx * nx + vy * ny;
        // Tangential speed survives, radial speed reverses outward — and never below a floor,
        // so a dead-vertical clip still leaves the lip instead of hovering on it.
        const outward = Math.max(Math.abs(radial) * RIM_RADIAL, RIM_MIN_KICK);
        const tangentX = vx - radial * nx;
        const tangentY = vy - radial * ny;
        vx = tangentX * RIM_TANGENT + outward * nx;
        vy = tangentY * RIM_TANGENT + outward * ny;
        vz = Math.abs(vz) * RIM_BOUNCE_UP;
        px = centre.x + nx * RIM_RADIUS;
        py = centre.y + ny * RIM_RADIUS;
        pz = BEERPONG_CUP_HEIGHT;
        rimmed.push(hit.cup);
      }
    } else if (pz < BEERPONG_CUP_HEIGHT) {
      // --- below the lip: cups are obstacles, not targets -------------------------------
      const hit = nearestCup(cups, remaining, px, py, RIM_RADIUS);
      if (hit && hit.distance > 0) {
        const centre = cups[hit.cup] as { x: number; y: number };
        const nx = (px - centre.x) / hit.distance;
        const ny = (py - centre.y) / hit.distance;
        const radial = vx * nx + vy * ny;
        if (radial < 0) {
          vx = (vx - 2 * radial * nx) * CUP_WALL_RESTITUTION;
          vy = (vy - 2 * radial * ny) * CUP_WALL_RESTITUTION;
          // One entry per *impact*: a ball rolling to a stop against a cup is in contact for
          // dozens of steps, and "rattled off cup 4 forty times" is neither true nor a
          // snapshot two clients could usefully compare.
          if (rimmed[rimmed.length - 1] !== hit.cup) rimmed.push(hit.cup);
        }
        px = centre.x + nx * RIM_RADIUS;
        py = centre.y + ny * RIM_RADIUS;
      }
    }

    // --- the table surface --------------------------------------------------------------
    if (!rolling && pz <= BEERPONG_BALL_RADIUS && vz < 0) {
      pz = BEERPONG_BALL_RADIUS;
      vz = -vz * TABLE_RESTITUTION;
      vx *= TABLE_FRICTION;
      vy *= TABLE_FRICTION;
      if (vz < ROLL_THRESHOLD) {
        vz = 0;
        rolling = true;
      }
    }

    // --- off the table --------------------------------------------------------------------
    if (px < 0 || px > BEERPONG_TABLE_LENGTH || py < 0 || py > BEERPONG_TABLE_WIDTH) {
      frames.push({ x: px, y: py, z: pz });
      steps += 1;
      break;
    }

    if ((steps + 1) % BEERPONG_FRAME_STRIDE === 0) frames.push({ x: px, y: py, z: pz });

    if (rolling && vx * vx + vy * vy < REST_SPEED * REST_SPEED) {
      vx = 0;
      vy = 0;
      frames.push({ x: px, y: py, z: pz });
      steps += 1;
      break;
    }
  }

  const outcome: BeerPongThrowOutcome = {
    cup: made,
    rimmed,
    rest: { x: quantizeBeerPong(px), y: quantizeBeerPong(py) },
  };
  return { outcome, frames, durationMs: steps * BEERPONG_PHYSICS_DT * 1000 };
}
